import React from 'react';
import { Card } from '@/components/ui/card';

const Guidelines: React.FC = () => {
  return (
    <div className="container mx-auto py-8 px-4">
      <h1 className="text-3xl font-bold mb-8">こんたくんフォーム作成ガイドライン</h1>

      <div className="space-y-8">
        {/* 基本設定 */}
        <section>
          <h2 className="text-2xl font-semibold mb-4 text-left">1. 基本設定</h2>
          
          <Card className="p-6 text-left">
            <h3 className="text-xl font-semibold mb-4 text-left">1.1 必須の設定</h3>
            <ul className="list-disc pl-6 mb-4">
              <li>フォームのid属性を <code className="bg-gray-100 px-2 py-1 rounded">contact-form</code> に設定してください</li>
              <li>このidはページ内で一意である必要があります</li>
              <li>特に、スクリプトタグのid（conta-kun-form）とフォームのid（contact-form）が重複しないよう注意してください</li>
            </ul>
            <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto">
              <code>{`<form id="contact-form">
  <!-- フォームの内容 -->
</form>`}</code>
            </pre>
          </Card>

          <Card className="p-6 mt-4 text-left">
            <h3 className="text-xl font-semibold mb-4 text-left">1.2 スクリプトの追加</h3>
            <p className="mb-4">フォームを使用するページに以下のスクリプトを追加してください。</p>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
              <p className="font-semibold">重要な注意点：</p>
              <ul className="list-disc pl-6">
                <li>スクリプトタグは必ずフォームの後に配置してください</li>
                <li>スクリプトのsrc属性には必ずバージョン（v1）を含めてください</li>
              </ul>
            </div>
            <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto">
              <code>{`<script
  id="conta-kun-form"
  src="https://static.conta-kun.4-two.com/v1/form.js"
  data-api-key="YOUR_API_KEY">
</script>`}</code>
            </pre>
            <p className="mt-2 text-sm text-gray-600">※ <code className="bg-gray-100 px-2 py-1 rounded">YOUR_API_KEY</code> は発行されたAPIキーに置き換えてください</p>
          </Card>

          <Card className="p-6 mt-4 text-left">
            <h3 className="text-xl font-semibold mb-4 text-left">1.3 必須項目</h3>
            <p className="mb-2">以下の項目を実装してください：</p>
            <ol className="list-decimal pl-6">
              <li>お名前（name属性: <code className="bg-gray-100 px-2 py-1 rounded">name</code>）</li>
              <li>メールアドレス（name属性: <code className="bg-gray-100 px-2 py-1 rounded">email</code>）</li>
              <li>お問い合わせ内容（name属性: <code className="bg-gray-100 px-2 py-1 rounded">content</code>）</li>
            </ol>
            <p className="mt-4">これらの項目には必ず <code className="bg-gray-100 px-2 py-1 rounded">required</code> 属性を付けてください。</p>
          </Card>
        </section>

        {/* フォーム要素の作成ルール */}
        <section>
          <h2 className="text-2xl font-semibold mb-4 text-left">2. フォーム要素の作成ルール</h2>
          
          <Card className="p-6 text-left">
            <h3 className="text-xl font-semibold mb-4 text-left">2.1 ラベルの設定方法</h3>
            <p className="mb-4">以下のいずれかの方法でラベルを設定してください。システムはこの順序で自動的にラベルを検出します：</p>
            
            <ol className="space-y-6">
              <li>
                <p className="font-medium">1. <code className="bg-gray-100 px-2 py-1 rounded">label[for]</code> 属性を使用した明示的なラベル（推奨）</p>
                <pre className="bg-gray-100 p-4 rounded-lg mt-2">
                  <code>{`<label for="name">お名前</label>
<input type="text" id="name" name="name" required>`}</code>
                </pre>
              </li>
              
              <li>
                <p className="font-medium">2. label要素で囲む</p>
                <pre className="bg-gray-100 p-4 rounded-lg mt-2">
                  <code>{`<label>
  メールアドレス
  <input type="email" name="email" required>
</label>`}</code>
                </pre>
              </li>
              
              <li>
                <p className="font-medium">3. 前の要素としてラベルを配置</p>
                <pre className="bg-gray-100 p-4 rounded-lg mt-2">
                  <code>{`<label>お問い合わせ内容</label>
<textarea name="content" required></textarea>`}</code>
                </pre>
              </li>
            </ol>
          </Card>

          <Card className="p-6 mt-4 text-left">
            <h3 className="text-xl font-semibold mb-4 text-left">2.2 グループ要素（ラジオボタン・チェックボックス）</h3>
            <p className="mb-4">ラジオボタンやチェックボックスをグループ化する場合は以下のように実装してください：</p>
            <pre className="bg-gray-100 p-4 rounded-lg">
              <code>{`<!-- ラジオボタングループの例 -->
<label>お問い合わせ種別</label>
<div class="radio-group">
  <label>
    <input type="radio" name="inquiry_type" value="general">
    一般的なお問い合わせ
  </label>
  <label>
    <input type="radio" name="inquiry_type" value="business">
    ビジネスに関するお問い合わせ
  </label>
</div>

<!-- チェックボックスグループの例 -->
<label>興味のある項目</label>
<div class="checkbox-group">
  <label>
    <input type="checkbox" name="interests[]" value="product1">
    製品1
  </label>
  <label>
    <input type="checkbox" name="interests[]" value="product2">
    製品2
  </label>
</div>`}</code>
            </pre>
          </Card>

          <Card className="p-6 mt-4 text-left">
            <h3 className="text-xl font-semibold mb-4 text-left">2.3 入力要素の命名規則</h3>
            <ul className="list-disc pl-6">
              <li>name属性は必ず指定してください</li>
              <li>チェックボックスグループの場合は <code className="bg-gray-100 px-2 py-1 rounded">name[]</code> の形式を使用してください</li>
              <li>半角英数字とアンダースコアのみを使用してください</li>
              <li>ラベルの自動検出のため、できるだけid属性も指定してください</li>
            </ul>
          </Card>

          <Card className="p-6 mt-4 text-left">
            <h3 className="text-xl font-semibold mb-4 text-left">2.4 非表示フィールド</h3>
            <p><code className="bg-gray-100 px-2 py-1 rounded">type="hidden"</code> の入力フィールドは確認画面に表示されません。</p>
          </Card>
        </section>

        {/* 複数フォームの実装 */}
        <section>
          <h2 className="text-2xl font-semibold mb-4 text-left">3. 複数フォームの実装</h2>
          
          <Card className="p-6 text-left">
            <h3 className="text-xl font-semibold mb-4 text-left">3.1 複数フォームを設置する場合の注意点</h3>
            <ul className="list-disc pl-6">
              <li>1ページ内に複数のフォームを設置する場合、<strong>id="contact-form"は1つのフォームにのみ</strong>設定できます</li>
              <li>複数のフォームで連携が必要な場合は、それぞれ別ページに分けることを推奨します</li>
              <li>1つのAPIキーで複数のフォームを管理する場合、別々のエンドポイントを取得する必要があります</li>
            </ul>
          </Card>

          <Card className="p-6 mt-4 text-left">
            <h3 className="text-xl font-semibold mb-4 text-left">3.2 複数フォーム実装時の対処方法</h3>
            <ol className="list-decimal pl-6 mb-4">
              <li>
                <p className="mb-2">複数のフォームが必要な場合は、以下のいずれかの方法を選択してください：</p>
                <ul className="list-disc pl-6 mb-4">
                  <li>別ページに分ける（推奨）</li>
                  <li>タブやアコーディオンで切り替える</li>
                  <li>JavaScriptで動的にid属性を切り替える</li>
                </ul>
              </li>
              <li>
                <p className="mb-2">動的に切り替える場合の実装例：</p>
                <pre className="bg-gray-100 p-4 rounded-lg mb-4">
                  <code>{`// フォームの切り替え処理
function switchForm(formType) {
  const forms = document.querySelectorAll('form');
  forms.forEach(form => {
    form.removeAttribute('id');
  });

  const targetForm = document.querySelector(\`form[data-type="\${formType}"]\`);
  if (targetForm) {
    targetForm.id = 'contact-form';
  }
}`}</code>
                </pre>
                <pre className="bg-gray-100 p-4 rounded-lg">
                  <code>{`<!-- HTML実装例 -->
<button onclick="switchForm('support')">サポート問い合わせ</button>
<button onclick="switchForm('business')">ビジネス問い合わせ</button>

<form data-type="support">
  <!-- サポート用フォーム -->
</form>

<form data-type="business">
  <!-- ビジネス用フォーム -->
</form>`}</code>
                </pre>
              </li>
            </ol>
          </Card>
        </section>

        {/* 動作の仕組み */}
        <section>
          <h2 className="text-2xl font-semibold mb-4 text-left">4. 動作の仕組み</h2>
          
          <Card className="p-6 text-left">
            <h3 className="text-xl font-semibold mb-4 text-left">4.1 フォーム送信時の挙動</h3>
            <ol className="list-decimal pl-6">
              <li>送信ボタンクリック時に確認モーダルが表示されます</li>
              <li>「送信する」を選択すると送信処理が開始されます</li>
              <li>送信中はローディングモーダルが表示されます</li>
              <li>送信完了後、完了モーダルが表示されます</li>
              <li>エラー発生時はエラーモーダルが表示されます</li>
            </ol>
          </Card>

          <Card className="p-6 mt-4 text-left">
            <h3 className="text-xl font-semibold mb-4 text-left">4.2 自動的に追加される機能</h3>
            <ul className="list-disc pl-6">
              <li>入力内容の確認画面</li>
              <li>送信中のローディング表示</li>
              <li>送信完了画面</li>
              <li>エラー表示</li>
              <li>送信ボタンの二重クリック防止</li>
            </ul>
          </Card>
        </section>

        {/* レスポンシブ対応 */}
        <section>
          <h2 className="text-2xl font-semibold mb-4 text-left">5. レスポンシブ対応</h2>
          <Card className="p-6 text-left">
            <ul className="list-disc pl-6">
              <li>モーダルは自動的にレスポンシブ対応されています</li>
              <li>フォーム自体のレスポンシブ対応は実装者側で行ってください</li>
            </ul>
          </Card>
        </section>

        {/* エラーハンドリング */}
        <section>
          <h2 className="text-2xl font-semibold mb-4 text-left">6. エラーハンドリング</h2>
          <Card className="p-6 text-left">
            <p className="mb-4">システムは以下のエラーを自動的に検出し、適切なエラーメッセージを表示します：</p>
            <ul className="list-disc pl-6">
              <li>APIキーが未指定の場合</li>
              <li>APIキーが無効の場合</li>
              <li>ネットワークエラー</li>
              <li>サーバーエラー</li>
            </ul>
          </Card>
        </section>

        {/* トラブルシューティング */}
        <section>
          <h2 className="text-2xl font-semibold mb-4 text-left">7. トラブルシューティング</h2>
          
          <Card className="p-6 text-left">
            <h3 className="text-xl font-semibold mb-4 text-left">7.1 よくある問題と解決方法</h3>
            <ol className="list-decimal pl-6">
              <li className="mb-4">
                <p className="font-medium">「APIキーが指定されていません」というエラーが表示される</p>
                <ul className="list-disc pl-6">
                  <li>スクリプトタグのdata-api-key属性を確認してください</li>
                  <li>フォームとスクリプトのidが重複していないか確認してください</li>
                  <li>スクリプトがフォームの後に配置されているか確認してください</li>
                </ul>
              </li>
              <li>
                <p className="font-medium">フォームが動作しない</p>
                <ul className="list-disc pl-6">
                  <li>フォームのidが <code className="bg-gray-100 px-2 py-1 rounded">contact-form</code> になっているか確認してください</li>
                  <li>スクリプトのURLに <code className="bg-gray-100 px-2 py-1 rounded">/v1/</code> が含まれているか確認してください</li>
                  <li>ブラウザの開発者ツールでエラーが出ていないか確認してください</li>
                </ul>
              </li>
            </ol>
          </Card>

          <Card className="p-6 mt-4 text-left">
            <h3 className="text-xl font-semibold mb-4 text-left">7.2 動作確認のポイント</h3>
            <ul className="list-disc pl-6">
              <li>フォーム送信前にHTML構造を確認してください</li>
              <li>必須項目（name, email, content）が正しく設定されているか確認してください</li>
              <li>ラベルが正しく関連付けられているか確認してください</li>
            </ul>
          </Card>
        </section>

        {/* 注意事項 */}
        <section>
          <h2 className="text-2xl font-semibold mb-4 text-left">8. 注意事項</h2>
          <Card className="p-6 text-left">
            <ul className="list-disc pl-6">
              <li>フォームのバリデーションは適切に実装してください</li>
              <li>必須項目（お名前、メールアドレス、お問い合わせ内容）には必ず <code className="bg-gray-100 px-2 py-1 rounded">required</code> 属性を付けてください</li>
              <li>フォームのスタイリングは実装者側で自由に行えます</li>
              <li>モーダルのスタイルは上書きできませんが、フォームの見た目には影響しません</li>
            </ul>
          </Card>
        </section>
      </div>
    </div>
  );
};

export default Guidelines;
